import { getMaxFileSize, getRequestTimeout } from '../config';

// Helper function to format duration in a user-friendly way
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';

  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  } else if (seconds < 3600) {
    const minutes = seconds / 60;
    return `${minutes.toFixed(1)}m`;
  } else {
    const hours = seconds / 3600;
    return `${hours.toFixed(1)}h`;
  }
};

// Helper function to format API key usage with precision for short durations
export const formatApiKeyUsage = (seconds) => {
  if (!seconds || seconds === 0) return '0s';

  if (seconds < 1) {
    // Show milliseconds for very short durations
    return `${(seconds * 1000).toFixed(0)}ms`;
  } else if (seconds < 60) {
    // Show seconds with 2 decimal places for precision
    return `${seconds.toFixed(2)}s`;
  } else if (seconds < 3600) {
    // Show minutes with 1 decimal place
    const minutes = seconds / 60;
    return `${minutes.toFixed(1)}m`;
  } else {
    // Show hours with 1 decimal place
    const hours = seconds / 3600;
    return `${hours.toFixed(1)}h`;
  }
};

// Helper function to get user-friendly error messages
export const getErrorMessage = (errorMessage) => {
  if (!errorMessage) return 'Unknown error occurred';

  // Parse common error patterns and provide user-friendly messages
  if (errorMessage.includes('Insufficient time remaining')) {
    return 'Not enough remaining time in your plan to process this audio file.';
  }
  // Match both old format "Audio duration" and new format "Audio file duration"
  if ((errorMessage.includes('Audio duration') || errorMessage.includes('Audio file duration')) && errorMessage.includes('exceeds')) {
    // Try new format first: "Audio file duration (X minutes) exceeds your remaining time (Y minutes)"
    let match = errorMessage.match(/Audio file duration \(([\d.]+) minutes\) exceeds your remaining time \(([\d.]+) minutes\)/);
    if (match) {
      return `Audio file is ${match[1]} minutes long, but you only have ${match[2]} minutes remaining.`;
    }
    // Fallback to old format: "Audio duration (X minutes) exceeds remaining time (Y minutes)"
    match = errorMessage.match(/Audio duration \(([\d.]+) minutes\) exceeds remaining time \(([\d.]+) minutes\)/);
    if (match) {
      return `Audio file is ${match[1]} minutes long, but you only have ${match[2]} minutes remaining.`;
    }
    return 'Audio file is longer than your remaining time allowance.';
  }
  if (errorMessage.includes('Failed to analyze audio file')) {
    return 'Unable to process the audio file. Please check the file format and try again.';
  }
  if (errorMessage.includes('File too large') || errorMessage.includes('File is too large') || errorMessage.includes('file too large')) {
    const maxFileSizeMB = (getMaxFileSize() / (1024 * 1024)).toFixed(0);
    return `File is too large. Maximum file size is ${maxFileSizeMB}MB.`;
  }
  // Handle backend format: "File too large: X bytes (max Y bytes)"
  if (errorMessage.match(/File too large.*max.*bytes/i)) {
    const maxFileSizeMB = (getMaxFileSize() / (1024 * 1024)).toFixed(0);
    return `File is too large. Maximum file size is ${maxFileSizeMB}MB.`;
  }
  if (errorMessage.includes('Failed to save file')) {
    return 'Unable to upload the file. Please try again.';
  }
  // Handle timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
    const timeoutSeconds = (getRequestTimeout() / 1000).toFixed(0);
    return `Request timed out after ${timeoutSeconds} seconds. The file might be too large or the server is busy. Please try again or contact support.`;
  }

  // Return the original message if no pattern matches
  return errorMessage;
};

// Helper function to get status color
export const getJobStatusColor = (status) => {
  switch (status) {
    case 'pending': return 'warning';
    case 'processing': return 'info';
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'cancelled': return 'default';
    default: return 'default';
  }
};

export const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
};

export const getTranscriptionText = (job) => {
    const transcription = job?.result_data?.transcription;
    if (!transcription) return '';
    if (typeof transcription === 'string') return transcription;
    return transcription.text || JSON.stringify(transcription);
};

// Helper function to format minutes as "X hours Y minutes Z seconds" (or appropriate combination)
export const formatMinutesAndSeconds = (minutes) => {
  if (!minutes || minutes === 0) return '0 seconds';
  
  const totalSeconds = Math.round(minutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  
  const parts = [];
  
  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (mins > 0) {
    parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`);
  }
  if (secs > 0 || parts.length === 0) {
    parts.push(`${secs} ${secs === 1 ? 'second' : 'seconds'}`);
  }
  
  return parts.join(' ');
};